import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../../api/api';
import SeasonSelector from '../../components/analytics/SeasonSelector';
import StatCard from '../../components/analytics/StatCard';

const HeadToHead = () => {
  const { t } = useTranslation();
  const [seasonId, setSeasonId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [team1, setTeam1] = useState('');
  const [team2, setTeam2] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/teams');
        setTeams(res.data);
      } catch (err) { /* ignore */ }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!team1 || !team2 || team1 === team2) { setData(null); return; }
    const fetch = async () => {
      setLoading(true);
      try {
        const params = `team1=${team1}&team2=${team2}${seasonId ? `&seasonId=${seasonId}` : ''}`;
        const res = await api.get(`/analytics/head-to-head?${params}`);
        setData(res.data);
      } catch (err) { setData(null); }
      setLoading(false);
    };
    fetch();
  }, [team1, team2, seasonId]);

  const chartData = data ? [
    { name: data.team1.abbreviation, wins: data.summary.team1Wins, avgScore: data.summary.team1AvgScore },
    { name: data.team2.abbreviation, wins: data.summary.team2Wins, avgScore: data.summary.team2AvgScore }
  ] : [];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">⚔️ {t('analytics.headToHead.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('analytics.headToHead.subtitle')}</p>
        </div>
        <SeasonSelector selectedSeasonId={seasonId} onSeasonChange={setSeasonId} />
      </div>

      {/* Team Selectors */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('analytics.headToHead.team1')}</label>
            <select
              value={team1}
              onChange={(e) => setTeam1(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">{t('analytics.headToHead.selectTeam')}</option>
              {teams.map(tm => (
                <option key={tm.id} value={tm.id}>{tm.city} {tm.name}</option>
              ))}
            </select>
          </div>
          <div className="text-center text-2xl font-bold text-gray-400 hidden md:block">VS</div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('analytics.headToHead.team2')}</label>
            <select
              value={team2}
              onChange={(e) => setTeam2(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">{t('analytics.headToHead.selectTeam')}</option>
              {teams.filter(tm => tm.id.toString() !== team1).map(tm => (
                <option key={tm.id} value={tm.id}>{tm.city} {tm.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>}

      {data && !loading && (
        <div>
          {data.summary.totalGames === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow">
              <p className="text-gray-500 dark:text-gray-400 text-lg">{t('analytics.headToHead.noGames')}</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6">
                <div className="grid grid-cols-3 gap-4 items-center text-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">{data.team1.city} {data.team1.name}</h3>
                    <p className="text-4xl font-bold text-blue-600 dark:text-blue-400 mt-2">{data.summary.team1Wins}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.cols.w')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">{t('analytics.headToHead.totalGames')}</p>
                    <p className="text-3xl font-bold text-gray-700 dark:text-gray-300">{data.summary.totalGames}</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">{data.team2.city} {data.team2.name}</h3>
                    <p className="text-4xl font-bold text-red-600 dark:text-red-400 mt-2">{data.summary.team2Wins}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.cols.w')}</p>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <StatCard label={`${data.team1.abbreviation} ${t('analytics.cols.ppg')}`} value={data.summary.team1AvgScore} color="blue" />
                <StatCard label={`${data.team2.abbreviation} ${t('analytics.cols.ppg')}`} value={data.summary.team2AvgScore} color="red" />
                <StatCard label={t('analytics.headToHead.totalPoints')} value={data.summary.team1Points + data.summary.team2Points} color="gray" />
                <StatCard label={t('analytics.cols.diff')} value={`${data.summary.pointDiff > 0 ? '+' : ''}${data.summary.pointDiff}`} color={data.summary.pointDiff > 0 ? 'blue' : 'red'} />
              </div>

              {/* Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">{t('analytics.headToHead.comparison')}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="wins" name={t('analytics.cols.w')} fill="#3B82F6" />
                    <Bar dataKey="avgScore" name={t('analytics.cols.ppg')} fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Match History */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
                <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 p-6 pb-0">{t('analytics.headToHead.matchHistory')}</h3>
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.date')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{data.team1.abbreviation}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">-</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{data.team2.abbreviation}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.diff')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.matches.map((m, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{m.date ? new Date(m.date).toLocaleDateString() : '-'}</td>
                        <td className={`px-4 py-3 text-sm font-bold ${m.winner === parseInt(team1) ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {m.team1Score} {m.team1IsHome ? `(${t('analytics.splits.homeShort')})` : ''}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-400">-</td>
                        <td className={`px-4 py-3 text-sm font-bold ${m.winner === parseInt(team2) ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {m.team2Score} {!m.team1IsHome ? `(${t('analytics.splits.homeShort')})` : ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{m.diff}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HeadToHead;
